/**
 * The Recurring tab of a cash account (TMMAPP-0102 §7; audit 03 "Recurring tab"; P v2 L1019–1170):
 * the series that land on this account (`Series.accountId`) with their allocations. A chip's value
 * and the "unallocated" chip come from the next occurrence's served `slices` and `remainderMinor`,
 * never percent × amount. A row expands to the next occurrences (on `surface2`); the narrow
 * variant stacks each series. Creating and changing series is subscriber-owner work (`canWrite`).
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { MoneyParts } from '@/components/MoneyText';
import { registerAmountColor } from '@/components/moneyTone';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Account } from '@/api/accounts';
import { ErrorCodes, isApiError } from '@/api/errors';
import { plannedApi, type Occurrence, type Series } from '@/api/planned';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorState } from '@/components/ErrorState';
import { KebabIcon } from '@/components/icons';
import { useOccurrenceHost } from '@/features/cashflow/useOccurrenceHost';
import { toCivil } from '@/features/envelope/window';
import { lineAmount } from '@/features/planned/paycheckParts';
import { plannedErrorKey } from '@/features/planned/plannedErrors';
import { describeRecurrence } from '@/features/planned/recurrence/recurrence';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { SubscriberActionHint, useSubscriberAction } from '@/session/useSubscriberAction';
import { ObjectMenu } from '@/shell/OverflowMenu';
import { resolveTokens } from '@/theme/theme';
import {
  addCivilDays,
  addCivilMonths,
  readWindow,
  useAppToday,
  WINDOW_MONTHS,
} from '@/shared/servedWindow';

/** Upcoming occurrences read for the chips and the expansion (a year ahead). */
const LOOKAHEAD_MONTHS = WINDOW_MONTHS;
const NEXT_SHOWN = 6;
const HIDDEN_STATES = new Set(['CANCELLED', 'SUPERSEDED']);

interface RecurringTabProps {
  account: Account;
  /**
   * The operating currency: the fallback when the account has none. An occurrence's parts and its
   * remainder are in the account's currency (`SliceView`, TMMAPP-0174).
   */
  operating: string | null;
  canWrite: boolean;
  reloadToken: number;
  onNotify: (message: string) => void;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; series: Series[]; occurrences: Occurrence[]; calendarError: unknown }
  | { status: 'error'; error: unknown };

function AllocationChip({
  name,
  value,
  onClick,
  size,
}: {
  name: string;
  value: ReactNode;
  onClick?: (() => void) | undefined;
  size: 'inline' | 'stacked';
}) {
  const tokens = resolveTokens(useTheme());
  const stacked = size === 'stacked';
  const sx = {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: stacked ? '6px' : '5px',
    maxWidth: '100%',
    minWidth: 0,
    minHeight: stacked ? 30 : 0,
    bgcolor: 'transparent',
    border: `1px solid ${tokens.line}`,
    borderRadius: '3px',
    p: stacked ? '5px 9px' : '2px 7px',
    color: tokens.text,
    font: 'inherit',
    whiteSpace: 'nowrap',
    '&:hover': onClick
      ? {
          borderColor: tokens.brandBoundary,
          backgroundImage: `linear-gradient(${tokens.selected}, ${tokens.selected})`,
        }
      : {},
    '&:focus-visible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
  } as const;
  const inner = (
    <>
      <Box
        component="span"
        sx={{
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontSize: stacked ? 12 : 11,
          color: tokens.text2,
        }}
      >
        {name}
      </Box>
      <Box
        component="span"
        sx={{
          flex: '0 0 auto',
          fontSize: stacked ? 12 : 11,
          fontWeight: 500,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Box>
    </>
  );
  return onClick ? (
    <Box component="button" type="button" onClick={onClick} sx={{ ...sx, cursor: 'pointer' }}>
      {inner}
    </Box>
  ) : (
    <Box component="span" sx={sx}>
      {inner}
    </Box>
  );
}

export function RecurringTab({
  account,
  operating,
  canWrite,
  reloadToken,
  onNotify,
}: RecurringTabProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const wide = useMediaQuery(theme.breakpoints.up('md'));
  const bank = account.currency ?? operating;
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [epoch, setEpoch] = useState(0);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [menu, setMenu] = useState<{ series: Series; el: HTMLElement } | null>(null);
  const [endFrom, setEndFrom] = useState<{ series: Series; date: string } | null>(null);
  const [cancelling, setCancelling] = useState<Series | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const tomorrow = addCivilDays(toCivil(new Date()), 1);

  const reload = useCallback(async () => setEpoch((n) => n + 1), []);
  const host = useOccurrenceHost({ account, currency: bank, onChanged: reload, onNotify });
  const create = useSubscriberAction(() => host.openCreate());

  const today = useAppToday();
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // Today forward by the one window rule (TMMAPP-0159 §0): `from` left out, one retry on a 422.
      // The two reads settle on their own (TMMAPP-0160 item 2): a calendar failure is the calendar's
      // section error and never blanks the series list.
      const [listed, calendar] = await Promise.allSettled([
        plannedApi.listSeries({ accountId: account.id, status: 'ACTIVE' }),
        readWindow(
          // Today + 12 months, never rounded up to a month's end: tmm-api's longest window
          // (TMMAPP-0171 item 6).
          { from: today, to: addCivilMonths(today, LOOKAHEAD_MONTHS) },
          today,
          (query) => plannedApi.calendar({ ...query, accountId: account.id }),
          [] as Occurrence[],
        ),
      ]);
      if (cancelled) return;
      if (listed.status === 'rejected') {
        const error: unknown = listed.reason;
        setState((prev) => (prev.status === 'ready' ? prev : { status: 'error', error }));
        return;
      }
      setState({
        status: 'ready',
        series: listed.value,
        occurrences: calendar.status === 'fulfilled' ? calendar.value : [],
        calendarError: calendar.status === 'rejected' ? (calendar.reason as unknown) : null,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [account.id, today, reloadToken, epoch]);

  const upcoming = useMemo(() => {
    const bySeries = new Map<number, Occurrence[]>();
    if (state.status !== 'ready') return bySeries;
    for (const occ of [...state.occurrences].sort((a, b) => a.occursOn.localeCompare(b.occursOn))) {
      if (HIDDEN_STATES.has(occ.state)) continue;
      bySeries.set(occ.seriesId, [...(bySeries.get(occ.seriesId) ?? []), occ]);
    }
    return bySeries;
  }, [state]);

  async function runConfirmed(fn: () => Promise<void>, okMessage: string, close: () => void) {
    if (busy) return;
    setBusy(true);
    setConfirmError(null);
    try {
      await fn();
      close();
      onNotify(okMessage);
      await reload();
    } catch (err) {
      if (isApiError(err) && err.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        close();
        onNotify(t('subscribe.requiresSubscription'));
      } else {
        setConfirmError(t(plannedErrorKey(err) ?? 'planned.errorGeneric'));
      }
    } finally {
      setBusy(false);
    }
  }

  const money = (minor: number, currency: string | null) => (
    <MoneyParts minor={minor} currency={currency} />
  );
  // A recurring amount (contract v2 §13): outgoing ink with a true minus, incoming `moneyPositive` with "+".
  const amountColor = (minor: number) => registerAmountColor(tokens, minor);
  const signedMoney = (minor: number, currency: string | null) => (
    <MoneyParts minor={minor} currency={currency} signed />
  );

  function chipsOf(occurrence: Occurrence | undefined, series: Series, size: 'inline' | 'stacked') {
    const slices = occurrence?.slices ?? [];
    const remainder = occurrence?.remainderMinor ?? 0;
    const edit = canWrite ? () => void host.openEditor('series', series.id) : undefined;
    return (
      <>
        {slices.map((slice) => (
          <AllocationChip
            key={slice.sliceRef}
            name={slice.envelopeName ?? '—'}
            value={money(slice.amountMinor, bank)}
            onClick={edit}
            size={size}
          />
        ))}
        {occurrence === undefined &&
          series.targets.map((target) => (
            <AllocationChip
              key={`t-${target.id}`}
              name={target.envelopeName ?? '—'}
              value="—"
              onClick={edit}
              size={size}
            />
          ))}
        {slices.length > 0 && remainder !== 0 && (
          <Box
            component="span"
            title={t('accountRecurring.unallocatedTip')}
            sx={{
              display: 'inline-flex',
              alignItems: 'baseline',
              gap: '5px',
              border: `1px dashed ${tokens.line}`,
              borderRadius: '3px',
              p: size === 'stacked' ? '5px 9px' : '2px 7px',
              fontSize: size === 'stacked' ? 12 : 11,
              color: tokens.text2,
              whiteSpace: 'nowrap',
            }}
          >
            <Box component="span" sx={{ fontStyle: 'italic' }}>
              {t('accountRecurring.unallocated')}
            </Box>
            <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {money(remainder, bank)}
            </Box>
          </Box>
        )}
        {slices.length === 0 && series.targets.length === 0 && canWrite && (
          <Box
            component="button"
            type="button"
            onClick={() => void host.openEditor('series', series.id)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              border: `1px dashed ${tokens.line}`,
              borderRadius: '3px',
              p: size === 'stacked' ? '5px 9px' : '3px 9px',
              bgcolor: 'transparent',
              color: tokens.link,
              font: 'inherit',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              '&:hover': {
                borderColor: tokens.brandBoundary,
                backgroundImage: `linear-gradient(${tokens.selected}, ${tokens.selected})`,
              },
              '&:focus-visible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
            }}
          >
            <Box component="span" aria-hidden sx={{ fontSize: 13, fontWeight: 300, lineHeight: 1 }}>
              +
            </Box>
            <Box component="span" sx={{ fontSize: 11, fontWeight: 500 }}>
              {t('accountRecurring.addAllocations')}
            </Box>
          </Box>
        )}
        {slices.length === 0 && series.targets.length === 0 && !canWrite && (
          <Box component="span" sx={{ fontSize: 11, fontStyle: 'italic', color: tokens.text2 }}>
            {t('accountRecurring.noAllocations')}
          </Box>
        )}
      </>
    );
  }

  const kebab = (series: Series, compact: boolean) =>
    canWrite ? (
      <IconButton
        aria-label={t('accountRecurring.actions', { name: series.name })}
        aria-haspopup="menu"
        onClick={(event) => setMenu({ series, el: event.currentTarget })}
        sx={{
          flex: '0 0 auto',
          width: compact ? 32 : 26,
          height: compact ? 32 : 26,
          p: 0,
          color: tokens.text2,
        }}
      >
        <KebabIcon size={compact ? 18 : 15} />
      </IconButton>
    ) : (
      <Box sx={{ flex: '0 0 26px' }} />
    );

  function seriesHead(series: Series) {
    return (
      <>
        <Stack direction="row" sx={{ alignItems: 'baseline', gap: '5px', minWidth: 0 }}>
          <Box
            component="span"
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: wide ? 13 : 15,
              fontWeight: 500,
            }}
          >
            {series.name}
          </Box>
          {series.targets.length > 1 && (
            <Box
              component="span"
              sx={{
                flex: '0 0 auto',
                typography: 'micro',
                color: tokens.link,
              }}
            >
              {t('accountRecurring.split')}
            </Box>
          )}
        </Stack>
        <Box
          component="span"
          sx={{
            display: 'block',
            mt: '1px',
            fontSize: wide ? 11 : 12,
            color: tokens.text2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: wide ? 'nowrap' : 'normal',
          }}
        >
          {describeRecurrence(series.recurrence, locale, t)}
        </Box>
      </>
    );
  }

  function occurrenceRows(series: Series) {
    const rows = (upcoming.get(series.id) ?? []).slice(0, NEXT_SHOWN);
    return (
      <Box
        data-occurrences={series.id}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          bgcolor: tokens.surface2,
          borderTop: `1px solid ${tokens.line}`,
        }}
      >
        {rows.map((occ) => (
          <Stack
            key={occ.originalOn}
            direction="row"
            sx={{
              alignItems: 'flex-start',
              gap: 1,
              p: wide ? '8px 18px 8px 48px' : '8px 16px',
              borderBottom: `1px solid ${tokens.line}`,
            }}
          >
            <Box
              component="span"
              sx={{
                flex: '0 0 56px',
                fontSize: 12,
                fontWeight: 500,
                fontVariantNumeric: 'tabular-nums',
                color: tokens.text,
              }}
            >
              {formatCivilDate(occ.occursOn, locale, { month: 'short', day: 'numeric' })}
            </Box>
            <Stack
              direction="row"
              sx={{
                flex: '1 1 auto',
                minWidth: 0,
                flexWrap: 'wrap',
                gap: '4px',
                alignItems: 'baseline',
              }}
            >
              {occ.slices.map((slice) => (
                <AllocationChip
                  key={slice.sliceRef}
                  name={slice.envelopeName ?? '—'}
                  value={money(slice.amountMinor, bank)}
                  size="inline"
                />
              ))}
              {occ.slices.length === 0 && (
                <Box
                  component="span"
                  sx={{ fontSize: 11, fontStyle: 'italic', color: tokens.text2 }}
                >
                  {t('accountRecurring.noAllocations')}
                </Box>
              )}
              {occ.differsFromSeries !== null &&
                (occ.differsFromSeries.date ||
                  occ.differsFromSeries.amount ||
                  occ.differsFromSeries.slices) && (
                  <Box component="span" sx={{ typography: 'micro', color: tokens.link }}>
                    {t('accountRecurring.edited')}
                  </Box>
                )}
              {/* A paycheck in parts (TMMAPP-0173 item 2): what arrived, or closed short. */}
              {occ.closedShort ? (
                <Box
                  component="span"
                  data-occurrence-part=""
                  sx={{ typography: 'micro', color: tokens.link }}
                >
                  {t('accountRecurring.closedShort')}
                </Box>
              ) : (
                occ.state === 'PARTIAL' &&
                occ.receivedMinor !== null && (
                  <Box
                    component="span"
                    data-occurrence-part=""
                    sx={{
                      typography: 'micro',
                      color: tokens.link,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {t('accountRecurring.partOf', {
                      received: lineAmount(occ.receivedMinor, bank, locale),
                      amount: lineAmount(occ.amountMinor, bank, locale),
                    })}
                  </Box>
                )
              )}
            </Stack>
            <Box
              component="span"
              sx={{
                flex: '0 0 86px',
                textAlign: 'end',
                fontSize: 12,
                fontWeight: 500,
                fontVariantNumeric: 'tabular-nums',
                color: amountColor(occ.amountMinor),
                whiteSpace: 'nowrap',
              }}
            >
              {signedMoney(occ.amountMinor, bank)}
            </Box>
            <IconButton
              aria-label={t('accountRecurring.openEvent', {
                date: formatCivilDate(occ.occursOn, locale),
              })}
              onClick={() => void host.openOccurrence(series.id, occ.originalOn)}
              sx={{ flex: '0 0 26px', width: 26, height: 26, p: 0, color: tokens.text2 }}
            >
              <KebabIcon size={14} />
            </IconButton>
          </Stack>
        ))}
        {rows.length === 0 && (
          <Box sx={{ p: '10px 18px 10px 48px', fontSize: 12, color: tokens.text2 }}>
            {t('accountRecurring.noUpcoming')}
          </Box>
        )}
      </Box>
    );
  }

  let body: ReactNode;
  if (state.status === 'loading') {
    body = <Skeleton variant="rectangular" height={140} />;
  } else if (state.status === 'error') {
    body = (
      <ErrorState
        variant="section"
        error={state.error}
        what={t('errors.what.planned')}
        onRetry={() => void reload()}
      />
    );
  } else if (state.series.length === 0) {
    body = (
      <Box
        sx={{ p: '26px 20px', fontSize: 13, color: tokens.text2, lineHeight: 1.6, maxWidth: 520 }}
      >
        {t('accountRecurring.empty')}
      </Box>
    );
  } else if (wide) {
    body = (
      <>
        <Stack
          aria-hidden
          direction="row"
          data-recurring-header=""
          sx={{
            alignItems: 'center',
            gap: 1,
            p: '7px 18px',
            borderBottom: `1px solid ${tokens.line}`,
            typography: 'micro',
            color: tokens.text2,
          }}
        >
          <Box sx={{ flex: '0 0 22px' }} />
          <Box sx={{ flex: '0 1 170px', minWidth: 0 }}>{t('accountRecurring.colSchedule')}</Box>
          <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>{t('accountRecurring.colAllocations')}</Box>
          <Box sx={{ flex: '0 0 86px', textAlign: 'end' }}>{t('accountRecurring.colPerEvent')}</Box>
          <Box sx={{ flex: '0 0 26px' }} />
        </Stack>
        {state.series.map((series) => {
          const next = upcoming.get(series.id)?.[0];
          const open = expanded === series.id;
          return (
            <Box key={series.id} sx={{ borderBottom: `1px solid ${tokens.line}` }}>
              <Stack
                direction="row"
                sx={{
                  alignItems: 'flex-start',
                  gap: 1,
                  p: '9px 18px',
                  '&:hover': { bgcolor: tokens.hover },
                }}
              >
                <Box
                  component="button"
                  type="button"
                  aria-expanded={open}
                  aria-label={t('accountRecurring.showNext', { name: series.name })}
                  onClick={() => setExpanded(open ? null : series.id)}
                  sx={{
                    flex: '0 0 22px',
                    width: 22,
                    height: 22,
                    mt: '2px',
                    bgcolor: 'transparent',
                    border: `1px solid ${tokens.line}`,
                    borderRadius: '3px',
                    color: tokens.text2,
                    cursor: 'pointer',
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: 13,
                    lineHeight: 1,
                    '&:hover': { borderColor: tokens.brandBoundary, color: tokens.link },
                    '&:focus-visible': {
                      outline: `2px solid ${tokens.focusRing}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  <span aria-hidden>{open ? '▾' : '▸'}</span>
                </Box>
                <Box sx={{ flex: '0 1 170px', minWidth: 0 }}>{seriesHead(series)}</Box>
                <Stack
                  direction="row"
                  sx={{ flex: '1 1 200px', minWidth: 0, flexWrap: 'wrap', gap: '4px', pt: '1px' }}
                >
                  {chipsOf(next, series, 'inline')}
                </Stack>
                <Box
                  component="span"
                  sx={{
                    flex: '0 0 86px',
                    textAlign: 'end',
                    fontSize: 13,
                    fontWeight: 500,
                    fontVariantNumeric: 'tabular-nums',
                    color: amountColor(series.amountMinor),
                    whiteSpace: 'nowrap',
                    pt: '1px',
                  }}
                >
                  {signedMoney(series.amountMinor, bank)}
                </Box>
                {kebab(series, false)}
              </Stack>
              {open && occurrenceRows(series)}
            </Box>
          );
        })}
      </>
    );
  } else {
    body = state.series.map((series) => {
      const next = upcoming.get(series.id)?.[0];
      const open = expanded === series.id;
      return (
        <Stack
          key={series.id}
          sx={{ gap: '9px', p: '14px 16px', borderBottom: `1px solid ${tokens.line}` }}
        >
          <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 1.25 }}>
            <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>{seriesHead(series)}</Box>
            <Box sx={{ flex: '0 0 auto', textAlign: 'end' }}>
              <Box component="div" sx={{ typography: 'micro', color: tokens.text2 }}>
                {t('accountRecurring.colPerEvent')}
              </Box>
              <Box
                component="div"
                sx={{
                  fontSize: 15,
                  fontWeight: 500,
                  fontVariantNumeric: 'tabular-nums',
                  color: amountColor(series.amountMinor),
                  mt: '1px',
                }}
              >
                {signedMoney(series.amountMinor, bank)}
              </Box>
            </Box>
            {kebab(series, true)}
          </Stack>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: '5px' }}>
            {chipsOf(next, series, 'stacked')}
          </Stack>
          <Button
            variant="text"
            size="small"
            aria-expanded={open}
            onClick={() => setExpanded(open ? null : series.id)}
            sx={{ alignSelf: 'flex-start' }}
          >
            {t('accountRecurring.showNext', { name: series.name })}
          </Button>
          {open && occurrenceRows(series)}
        </Stack>
      );
    });
  }

  return (
    <Paper sx={{ p: 0, overflow: 'hidden' }}>
      <Stack
        direction="row"
        data-card-header=""
        sx={{
          alignItems: 'baseline',
          gap: '10px',
          flexWrap: 'wrap',
          p: '14px 18px',
          borderBottom: `1px solid ${tokens.line}`,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Typography variant="h3" component="h2">
            {t('accountRecurring.title')}
          </Typography>
          <Typography variant="caption" component="p" sx={{ color: tokens.text2 }}>
            {t('accountRecurring.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          aria-describedby={create.descriptionId}
          onClick={create.run}
          sx={{ flex: '0 0 auto' }}
        >
          {t('accountRecurring.new')}
        </Button>
        <SubscriberActionHint id={create.descriptionId} description={create.description} />
      </Stack>
      {state.status === 'ready' && state.calendarError !== null && (
        <Box sx={{ p: '12px 18px 0' }}>
          <ErrorState
            variant="section"
            error={state.calendarError}
            what={t('errors.what.planned')}
            onRetry={() => void reload()}
          />
        </Box>
      )}
      {body}

      <ObjectMenu
        title={menu?.series.name}
        anchorEl={menu?.el}
        open={menu !== null}
        onClose={() => setMenu(null)}
      >
        <MenuItem
          onClick={() => {
            if (menu) void host.openEditor('series', menu.series.id);
            setMenu(null);
          }}
        >
          {t('planned.editSeries')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menu) {
              setConfirmError(null);
              setEndFrom({ series: menu.series, date: tomorrow });
            }
            setMenu(null);
          }}
        >
          {t('planned.endFrom')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menu) {
              setConfirmError(null);
              setCancelling(menu.series);
            }
            setMenu(null);
          }}
        >
          {t('planned.cancelSeries')}
        </MenuItem>
      </ObjectMenu>

      {host.node}

      {endFrom !== null && (
        <ConfirmDialog
          title={t('planned.endFromSeriesTitle', { name: endFrom.series.name })}
          consequence={t('planned.endFromConsequence')}
          confirmLabel={t('planned.endSeries')}
          destructive
          busy={busy}
          error={confirmError}
          confirmDisabled={endFrom.date < tomorrow}
          onConfirm={() =>
            void runConfirmed(
              () => plannedApi.endFrom(endFrom.series.id, { fromDate: endFrom.date }),
              t('planned.seriesEnded'),
              () => setEndFrom(null),
            )
          }
          onCancel={() => setEndFrom(null)}
        >
          <TextField
            type="date"
            label={t('planned.endFromDate')}
            value={endFrom.date}
            onChange={(event) => setEndFrom({ ...endFrom, date: event.target.value })}
            disabled={busy}
            error={endFrom.date < tomorrow}
            helperText={endFrom.date < tomorrow ? t('planned.endFromPast') : undefined}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: tomorrow } }}
            fullWidth
          />
        </ConfirmDialog>
      )}

      {cancelling !== null && (
        <ConfirmDialog
          title={t('planned.cancelSeriesTitle', { name: cancelling.name })}
          consequence={t('planned.cancelSeriesConsequence')}
          confirmLabel={t('planned.cancelSeries')}
          cancelLabel={t('planned.keepSeries')}
          destructive
          busy={busy}
          error={confirmError}
          onConfirm={() =>
            void runConfirmed(
              () => plannedApi.cancelSeries(cancelling.id),
              t('planned.seriesCancelled'),
              () => setCancelling(null),
            )
          }
          onCancel={() => setCancelling(null)}
        />
      )}
    </Paper>
  );
}
